import { useEffect, useState } from 'react';
import { Modal } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/input';
import { api } from '@/lib/utils';
import { STRINGS, type EarlyAccessFeature } from '@maintenance-tracker/shared';

type EarlyRequest = {
  id: string;
  feature: string;
  details: Record<string, unknown>;
  withdrawnAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

export function EarlyAccessModal({
  open,
  onClose,
  feature,
  title,
  description,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  feature: EarlyAccessFeature;
  title?: string;
  description?: string;
  onSaved?: (req: EarlyRequest | null) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState<EarlyRequest | null>(null);
  const [interests, setInterests] = useState<string[]>([]);
  const [platform, setPlatform] = useState('');
  const [leadsPerMonth, setLeadsPerMonth] = useState('');
  const [capabilitySuggestion, setCapabilitySuggestion] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!open) return;
    setMessage('');
    void api<{ request: EarlyRequest | null }>(`/api/v1/early-access/${feature}`)
      .then((d) => {
        const r = d.request && !d.request.withdrawnAt ? d.request : null;
        setExisting(r);
        if (r?.details) {
          setInterests(Array.isArray(r.details.interests) ? (r.details.interests as string[]) : []);
          setPlatform(typeof r.details.platform === 'string' ? r.details.platform : '');
          setLeadsPerMonth(typeof r.details.leadsPerMonth === 'string' ? r.details.leadsPerMonth : '');
          setCapabilitySuggestion(
            typeof r.details.capabilitySuggestion === 'string' ? r.details.capabilitySuggestion : '',
          );
        }
      })
      .catch(() => undefined);
  }, [open, feature]);

  const interestOptions =
    feature === 'website_insights'
      ? [
          { id: 'gsc_ga', label: 'Search Console and Analytics' },
          { id: 'suggestions', label: 'Intelligent suggestions' },
          { id: 'ai_content', label: 'AI content help' },
        ]
      : [
          { id: 'look_professional', label: 'Look more professional' },
          { id: 'local_discovery', label: 'Improve local discovery' },
          { id: 'post_consistently', label: 'Post consistently' },
          { id: 'showcase', label: 'Showcase projects' },
          { id: 'understand_content', label: 'Understand what content works' },
          { id: 'more_inquiries', label: 'Get more inquiries' },
        ];

  const submit = async (withdraw = false) => {
    setBusy(true);
    setMessage('');
    try {
      const data = await api<{ ok: boolean; message: string; request: EarlyRequest }>(
        `/api/v1/early-access/${feature}`,
        {
          method: 'POST',
          body: JSON.stringify({
            interests,
            platform,
            leadsPerMonth,
            capabilitySuggestion,
            withdraw,
            consentToContact: true,
          }),
        },
      );
      setMessage(data.message);
      const next = withdraw ? null : data.request;
      setExisting(next);
      onSaved?.(next);
      if (withdraw) onClose();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title ?? 'Request early access'}
      description={description ?? STRINGS.earlyAccess.notAvailable}
    >
      <div className="space-y-3">
        {interestOptions.map((i) => (
          <label key={i.id} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={interests.includes(i.id)}
              onChange={(e) => {
                setInterests((prev) =>
                  e.target.checked ? [...prev, i.id] : prev.filter((x) => x !== i.id),
                );
              }}
            />
            {i.label}
          </label>
        ))}
        <label className="block space-y-1 text-sm">
          <span className="font-medium">Website / platform (optional)</span>
          <Select value={platform} onChange={(e) => setPlatform(e.target.value)}>
            <option value="">Select…</option>
            <option value="wordpress">WordPress</option>
            <option value="wix">Wix</option>
            <option value="squarespace">Squarespace</option>
            <option value="godaddy">GoDaddy</option>
            <option value="other">Other</option>
          </Select>
        </label>
        <label className="block space-y-1 text-sm">
          <span className="font-medium">Rough leads per month (optional)</span>
          <Input value={leadsPerMonth} onChange={(e) => setLeadsPerMonth(e.target.value)} />
        </label>
        <label className="block space-y-1 text-sm">
          <span className="font-medium">What would help you most?</span>
          <Input
            value={capabilitySuggestion}
            onChange={(e) => setCapabilitySuggestion(e.target.value)}
          />
        </label>
        {message && <p className="text-sm text-text-secondary">{message}</p>}
        <div className="flex flex-wrap gap-2 pt-2">
          <Button disabled={busy} onClick={() => void submit(false)}>
            {existing ? 'Update my request' : 'Request early access'}
          </Button>
          {existing && (
            <Button variant="secondary" disabled={busy} onClick={() => void submit(true)}>
              Withdraw
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}
